import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import SpeciesSelect from "~/shared/components/SpeciesSelect";

type Props = {
    error?: ApolloError;
    onSubmit: (value: ResearchTypeCreateFormInputs) => void;
};

export default function ResearchTypeCreateForm({ error, onSubmit }: Props) {
    const { t } = useTranslation("researchType");

    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<ResearchTypeCreateFormInputs>({
        defaultValues: { name: "", description: "", requireRecordings: false, speciesIds: [] },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("name")}
                    label={t("researchTypeCreateForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />

                <TextField
                    {...register("description")}
                    label={t("researchTypeCreateForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    multiline
                />

                <SwitchElement
                    name="requireRecordings"
                    label={t("researchTypeCreateForm.labelRequireRecordings")}
                    control={control}
                />
                <Controller
                    control={control}
                    name="speciesIds"
                    render={({ field }) => (
                        <SpeciesSelect
                            value={field.value}
                            multiple={true}
                            label={t("researchTypeCreateForm.labelSpecies")}
                            onChange={field.onChange}
                            disabled={false}
                            error={!!errors.speciesIds}
                            helperText={errors.speciesIds?.message}
                        />
                    )}
                />

                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("researchTypeCreateForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type ResearchTypeCreateFormInputs = {
    name: string;
    description: string;
    requireRecordings: boolean;
    speciesIds: string[];
};
